import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDailyMenu, getLocalDateId } from "../services/admin.service";
import { AdminDailyMenu, CartItem } from "../types/index";
import { Spinner } from "../components/common/Spinner";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { SafeImage } from "../components/common/SafeImage";

type KitchenGroup = { kitchenId: string; kitchenName: string; items: CartItem[] };

const DailyMenu: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dailyMenu, setDailyMenu] = useState<AdminDailyMenu | null>(null);
  const [error, setError] = useState<string | null>(null);

  const todayId = useMemo(() => getLocalDateId(new Date()), []);
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const menu = await getDailyMenu(todayId);
        if (!cancelled) setDailyMenu(menu);
      } catch (err) {
        console.error("Failed to load daily menu", err);
        if (!cancelled) setError("Could not load today's menu. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [todayId]);

  const groups: KitchenGroup[] = useMemo(() => {
    if (!dailyMenu?.kitchenMenus) return [];
    return Object.entries(dailyMenu.kitchenMenus)
      .map(([kitchenId, group]) => ({
        kitchenId,
        kitchenName: group.kitchenName || "Kitchen",
        items: Array.isArray(group.items) ? group.items : [],
      }))
      .filter((g) => g.items.length > 0);
  }, [dailyMenu]);

  const totalItems = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8 scroll-mt-[calc(var(--ruchira-header-height)+1rem)]">
          <p className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-1">
            Today&apos;s Daily Menu
          </p>
          <h1 className="text-3xl font-bold text-gray-900">Today&apos;s Specials</h1>
          <p className="text-gray-500 mt-1">{todayLabel}</p>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center min-h-[40vh]">
            <Spinner size={48} />
            <p className="mt-4 text-gray-500">Loading today&apos;s menu…</p>
          </div>
        )}

        {!loading && error && (
          <Card className="p-8 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </Card>
        )}

        {!loading && !error && groups.length === 0 && (
          <Card className="p-10 text-center">
            <div className="text-4xl mb-3">🍱</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">No daily menu yet</h2>
            <p className="text-gray-500 mb-6 text-sm">
              Chefs haven&apos;t published today&apos;s specials. Browse kitchens or start a
              subscription plan meanwhile.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="primary" onClick={() => navigate({ pathname: "/", hash: "kitchen-grid" })}>
                Browse Kitchens
              </Button>
              <Button variant="secondary" onClick={() => navigate("/subscription")}>
                View Subscription
              </Button>
            </div>
          </Card>
        )}

        {!loading && !error && groups.length > 0 && (
          <div className="space-y-8">
            <p className="text-sm text-gray-500">
              {totalItems} item{totalItems === 1 ? "" : "s"} from {groups.length} kitchen
              {groups.length === 1 ? "" : "s"}
            </p>

            {groups.map((group) => (
              <section
                key={group.kitchenId}
                className="scroll-mt-[calc(var(--ruchira-header-height)+1rem)]"
              >
                <div className="flex items-center justify-between mb-3 gap-3">
                  <h2 className="text-lg font-bold text-gray-900">{group.kitchenName}</h2>
                  <button
                    type="button"
                    onClick={() => navigate(`/kitchen/${group.kitchenId}`)}
                    className="text-xs font-bold text-orange-600 hover:text-orange-700 min-h-[44px] px-3"
                  >
                    Open kitchen →
                  </button>
                </div>

                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {group.items.map((item) => (
                    <Card
                      key={item.id || `${group.kitchenId}-${item.name}`}
                      className="p-0 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigate(`/kitchen/${item.kitchenId || group.kitchenId}`)}
                    >
                      <div className="h-36 bg-gray-100 overflow-hidden">
                        <SafeImage
                          src={item.photoUrl}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex justify-between items-start gap-2">
                          <h3 className="font-bold text-gray-900 leading-snug">{item.name}</h3>
                          <span className="font-bold text-green-600 whitespace-nowrap">
                            ₹{item.price}
                          </span>
                        </div>
                        {item.category && (
                          <span className="inline-block mt-2 text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 px-2 py-0.5 rounded capitalize">
                            {item.category}
                          </span>
                        )}
                        {item.description && (
                          <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            ))}

            <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
              <Button variant="secondary" onClick={() => navigate("/")}>
                Back to Home
              </Button>
              <Button variant="primary" onClick={() => navigate("/subscription")}>
                Build a Subscription
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyMenu;
